import type {
  CreateHierarchyValueProps,
  GlobalHierarchyFilterStructure,
  GlobalHierarchyFiltersResponse,
  HierarchyType,
  HierarchyTypesApiProps,
  HierarchyTypesResult,
  HierarchyValuesApiProps,
  HierarchyValuesResult,
} from '../../@types/hierarchy-fields';
import type { OptionPostResponse } from '../../@types/options';
import { isOfflineEnabled } from '../../db/odin-db-util';
import { createFieldFilterText } from '../../utils/filter-url-builder-utils';
import { formatGlobalHierarchyFiltersData } from '../../utils/hierarchy-functions';
import { api } from './api';

export const hierarchyApi = api
  .enhanceEndpoints({ addTagTypes: ['GlobalHierarchyFilters', 'HierarchyValues'] })
  .injectEndpoints({
    overrideExisting: 'throw',
    endpoints: (builder) => ({
      hierarchyTypes: builder.query<Array<HierarchyType>, HierarchyTypesApiProps>({
        query: (args) => {
          const url = '/hierarchy-types/simple';
          let queryParams = '?';
          if (args?.archived) {
            queryParams += `&filter=archived:eq:${args.archived}`;
          }
          return url + (queryParams.length > 1 ? queryParams : '');
        },
        transformResponse: (response: HierarchyTypesResult) => {
          return response.result;
        },
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          if (!isOfflineEnabled) {
            return;
          }

          const { data } = await queryFulfilled;
          data.forEach((hierarchyType) => {
            // prefetch hierarchy values for each hierarchy type
            dispatch(
              hierarchyApi.util.prefetch(
                'hierarchyValues',
                // args need to match component api call to ensure cache hits
                {
                  page: 1,
                  pageSize: 50,
                  sort: 'caption',
                  filters: {
                    typeId: { value: hierarchyType.id, comparison: '=' },
                    archived: { value: false, comparison: '=' },
                  },
                },
                { force: true }
              )
            );
          });
        },
      }),
      hierarchyValues: builder.query<HierarchyValuesResult, HierarchyValuesApiProps>({
        query: ({ page, pageSize, sort, filters }) => {
          let url = `/hierarchy-values/simple?page=${page}&pageSize=${pageSize}${createFieldFilterText({ filters })}`;
          if (sort) {
            url += `&sort=${sort}`;
          }
          return url;
        },
        transformResponse: (response: HierarchyValuesResult) => {
          return { ...response, items: response?.items ?? [] };
        },
        providesTags: (result) =>
          result
            ? [
                ...result.items.map((item) => ({ type: 'HierarchyValues' as const, id: item.hierarchyTypeId })),
                { type: 'HierarchyValues', id: 'LIST' } as const,
              ]
            : [{ type: 'HierarchyValues', id: 'LIST' } as const],
      }),
      getGlobalHierarchyFilters: builder.query<GlobalHierarchyFilterStructure, void>({
        query: () => '/global-hierarchy-filters',
        transformResponse: (response: GlobalHierarchyFiltersResponse) =>
          formatGlobalHierarchyFiltersData(response.result),
        providesTags: ['GlobalHierarchyFilters'],
      }),
      updateGlobalHierarchyFilters: builder.mutation<void, Omit<GlobalHierarchyFilterStructure, 'id'>>({
        query: (body) => ({
          url: '/global-hierarchy-filters',
          method: 'PUT',
          body: body,
        }),
        invalidatesTags: ['GlobalHierarchyFilters'],
      }),
      deleteGlobalHierarchyFilters: builder.mutation<void, void>({
        query: () => ({
          url: '/global-hierarchy-filters',
          method: 'DELETE',
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        invalidatesTags: ['GlobalHierarchyFilters'],
      }),
      createHierarchyValue: builder.mutation<OptionPostResponse, CreateHierarchyValueProps>({
        query: ({ hierarchyTypeId, ...body }) => ({
          url: `/hierarchy-types/${hierarchyTypeId}/values/simple`,
          method: 'POST',
          body: body,
        }),
        invalidatesTags: (result, error, arg) => {
          if (result) {
            return [{ type: 'HierarchyValues', id: arg.hierarchyTypeId }];
          } else {
            return [];
          }
        },
      }),
    }),
  });

export const {
  useHierarchyTypesQuery,
  useLazyHierarchyTypesQuery,
  useHierarchyValuesQuery,
  useLazyHierarchyValuesQuery,
  useGetGlobalHierarchyFiltersQuery,
  useUpdateGlobalHierarchyFiltersMutation,
  useDeleteGlobalHierarchyFiltersMutation,
  useCreateHierarchyValueMutation,
} = hierarchyApi;
